/* CopyPaymentLinkButton
 * One-click copy of a Stripe Payment Link URL with a brief "Copied!" toast.
 * Used in Admin → Catalog rows so sales teams can grab a `buy.stripe.com`
 * URL for a specific product/tier and drop it straight into emails or SMS.
 *
 * Two modes:
 *   - Single-tier (e.g. Services products: just one base price)
 *       <CopyPaymentLinkButton url={url} label="Copy" />
 *
 *   - Multi-tier (e.g. Systems services: essential / professional / enterprise)
 *       <CopyPaymentLinkButton tiers={{essential: {url, ...}, ...}} />
 *     Renders a small dropdown so the operator picks which tier's link to copy.
 */
import React, { useState } from 'react';
import { Link2, Check, ChevronDown, Lock } from 'lucide-react';

const PILL_CLASS =
  'inline-flex items-center gap-1 rounded-full bg-purple-100 px-3 py-1.5 text-[11.5px] font-semibold text-purple-900 hover:bg-purple-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed';

function copyToClipboard(text) {
  if (navigator.clipboard?.writeText) return navigator.clipboard.writeText(text);
  // Fallback for non-HTTPS/older browsers
  const ta = document.createElement('textarea');
  ta.value = text;
  ta.style.position = 'fixed';
  ta.style.opacity = '0';
  document.body.appendChild(ta);
  ta.select();
  try { document.execCommand('copy'); } finally { document.body.removeChild(ta); }
  return Promise.resolve();
}

export default function CopyPaymentLinkButton({ url, tiers, slug, testIdPrefix = 'copy-paylink' }) {
  const [copied, setCopied] = useState(false);
  const [open, setOpen] = useState(false);

  const handleCopy = async (text) => {
    if (!text) return;
    await copyToClipboard(text);
    setCopied(true);
    setOpen(false);
    setTimeout(() => setCopied(false), 1500);
  };

  // Tiered mode — render a dropdown picker
  if (tiers && Object.keys(tiers).length > 0) {
    const tierEntries = Object.entries(tiers);
    return (
      <div className="relative">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className={PILL_CLASS}
          data-testid={`${testIdPrefix}-${slug}-tiered`}
          title="Copy a Stripe Payment Link for one of the tiers"
        >
          {copied ? <><Check className="h-3 w-3" /> Copied!</> : <><Link2 className="h-3 w-3" /> Pay link <ChevronDown className="h-3 w-3" /></>}
        </button>
        {open && (
          <div className="absolute right-0 z-10 mt-1 min-w-[200px] rounded-xl border border-gray-200 bg-white shadow-lg overflow-hidden" data-testid={`${testIdPrefix}-${slug}-menu`}>
            {tierEntries.map(([tierId, info]) => (
              <button
                key={tierId}
                type="button"
                onClick={() => handleCopy(info.url)}
                className="w-full flex items-center justify-between gap-2 px-3 py-2 text-[12px] text-left hover:bg-purple-50 border-b border-gray-100 last:border-0"
                data-testid={`${testIdPrefix}-${slug}-tier-${tierId}`}
              >
                <span className="font-semibold text-gray-900 capitalize">{tierId}</span>
                <span className="text-[10.5px] font-mono text-gray-500 truncate max-w-[100px]">
                  {(info.url || '').replace('https://', '').slice(0, 22)}…
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Single-tier mode — direct copy button
  if (!url) {
    return (
      <span
        className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2.5 py-1.5 text-[11px] font-semibold text-gray-500"
        title="No Stripe Payment Link yet — run Admin → Services → Re-generate Payment Links"
        data-testid={`${testIdPrefix}-${slug}-empty`}
      >
        <Lock className="h-3 w-3" /> No link
      </span>
    );
  }
  return (
    <button
      type="button"
      onClick={() => handleCopy(url)}
      className={PILL_CLASS}
      data-testid={`${testIdPrefix}-${slug}`}
      title={`Copy Stripe Payment Link\n${url}`}
    >
      {copied ? <><Check className="h-3 w-3" /> Copied!</> : <><Link2 className="h-3 w-3" /> Copy link</>}
    </button>
  );
}
